import { useMemo, useState } from 'react';
import { MenuHeader } from './MenuHeader.jsx';
import { CategoryNav } from './CategoryNav.jsx';
import { CategoryList } from './CategoryList.jsx';
import { ItemDetailSheet } from './ItemDetailSheet.jsx';
import { resolveAppearance, appearanceToCssVariables } from '../theme/applyAppearanceConfig.js';
import { useItemSelectionState } from '../hooks/useItemSelectionState.js';

/**
 * The single component both the public site and the admin live-preview
 * render — this is what guarantees preview never visually diverges from
 * what publishing would actually produce (spec §12/§33).
 *
 * @param {object} props.menuData - `{ tenant, menu, appearance, categories }`,
 *   the exact shape produced by the server's buildMenuTree() for both a
 *   published snapshot and a draft preview.
 */
export function MenuExperience({ menuData, initialCategory }) {
  const { tenant, menu, appearance, categories } = menuData;
  const [searchQuery, setSearchQuery] = useState('');
  const resolved = resolveAppearance(appearance);
  const initialCategoryId = initialCategory
    ? categories.find((category) => category.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-') === initialCategory.toLowerCase())?.id
    : undefined;
  const { selectedItem, openItem, closeItem, activeCategoryId, scrollToCategory } = useItemSelectionState(categories, initialCategoryId);
  const visibleCategories = useMemo(() => filterCategories(categories, searchQuery), [categories, searchQuery]);
  const resultCount = visibleCategories.reduce((total, category) => total + category.items.length, 0);

  return (
    <div
      className="rm-root"
      style={appearanceToCssVariables(appearance)}
      data-theme-preset={resolved.theme.preset}
      data-density={resolved.layout.density}
      data-image-shape={resolved.layout.imageShape}
    >
      <MenuHeader tenant={tenant} menu={menu} appearance={resolved} />
      <div className="rm-container">
        <CategoryNav
          categories={categories}
          visibleCategories={visibleCategories}
          activeCategoryId={activeCategoryId}
          onSelect={scrollToCategory}
          variant={resolved.layout.categoryNavigation}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          resultCount={resultCount}
        />
        <div className="rm-menu-content">
          <CategoryList
            categories={visibleCategories}
            currency={tenant.currency}
            layout={resolved.layout}
            itemDisplay={resolved.itemDisplay}
            onOpenItem={openItem}
            searchQuery={searchQuery}
          />
        </div>
      </div>
      {selectedItem && (
        <ItemDetailSheet item={selectedItem} currency={tenant.currency} itemDisplay={resolved.itemDisplay} onClose={closeItem} />
      )}
    </div>
  );
}

function filterCategories(categories, searchQuery) {
  const query = searchQuery.trim().toLowerCase();
  if (!query) return categories;

  return categories
    .map((category) => ({
      ...category,
      items: category.items.filter((item) => {
        const attributes = (item.attributes ?? []).map((attr) => `${attr.label} ${attr.value}`).join(' ');
        return [item.name, item.description, attributes].filter(Boolean).join(' ').toLowerCase().includes(query);
      }),
    }))
    .filter((category) => category.items.length > 0);
}
